// @flow

import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";
import actionCreators from "@modules/profile/actionCreator";
import sharedActionCreators from "@modules/shared/actionCreator";
import accountActionCreators from "@modules/account/actionCreator";
import type { MyAbsenceDraft } from "@modules/profile/reducer/my-absences/types";
import * as selectors from "@modules/profile/selectors";

import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  LinearWizard,
  Step,
} from "@components";

import MyAbsencesMainDetails from "./main-details";
import MyAbsenceShiftPatterns from "./shift-patterns";
import MyClassifications from "./classifications";
import MyAdditionalInformation from "./additional-information";

type OwnProps = $Exact<{
  clientId: number,
  userId: number,
  isEdit: boolean,
  onClose: () => void,
  defaultStep: number,
}>;

type PropsFromState = $Exact<{
  schema: Object | null,
  draft: MyAbsenceDraft | null,
  isValid: boolean,
  absence: Object | null,
  standardQuestions: Array<any> | null,
  dynamicQuestions: Array<any> | null,
}>;

type PropsFromDispatch = $Exact<{
  getMyClassificationsSchema: typeof actionCreators.GetMyClassificationsSchema,
  getActiveShiftPattern: typeof actionCreators.GetUserActiveShiftPattern,
  getEmployeeBankHolidays: typeof sharedActionCreators.GetEmployeeBankHolidaysOptions,
  onUpdateDraft: typeof actionCreators.SetMyAbsenceDraft,
  setShowCancelMyAbsencesModal: typeof actionCreators.SetShowCancelMyAbsencesModal,
  submitDynamicQuestions: typeof actionCreators.AddMyDynamicQuestions,
  submitStandardQuestions: typeof actionCreators.AddMyStandardQuestions,
  submitAdditionalQuestion: typeof actionCreators.AddMyAdditionalInfoQuestion,
  getAccountClientServiceSettings: typeof accountActionCreators.GetAccountClientServiceSettings,
  getAccountAbsenceManagementSettings: typeof accountActionCreators.GetAccountAbsenceManagementSettings,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
}>;

function AddMyAbsencesModal(props: Props) {
  const absenceType = useRef(null);

  // if current absence exist and have an interactionId, allow to cancel the absence
  // interactionId exist only when we are creating a new absence
  // or when editing existing absence, and we submit the new data, then we receive a new interactionID
  // if updating current absence, which is not just created, isEdit = true
  const allowCancelAbsence: boolean = !!(
    !props.isEdit &&
    props.absence &&
    props.absence?.interactionId
  );

  useEffect(() => {
    const { clientId, userId, draft } = props;
    const employeeId = userId;

    props.getEmployeeBankHolidays({
      clientId,
      employeeId,
    });

    props.getActiveShiftPattern({
      clientId,
      employeeId,
    });

    props.getAccountClientServiceSettings({
      clientId,
    });

    props.getAccountAbsenceManagementSettings({
      clientId,
    });

    if (props.isEdit && draft?.absenceType) {
      props.getMyClassificationsSchema({
        clientId,
        absenceReasonType: draft?.absenceType,
      });
    }
  }, []);

  useEffect(() => {
    const { clientId, draft } = props;

    // if the absence type is changed, load a new classifications schema
    if (draft && draft.absenceType !== absenceType.current) {
      absenceType.current = draft.absenceType;

      props.getMyClassificationsSchema({
        clientId,
        absenceReasonType: draft.absenceType,
      });
    }
  }, [props.draft]);

  const [questions, setQuestions] = useState({
    standard: {},
    dynamic: {},
    referrals: {},
    additionalInfo: "",
  });

  const [isShiftPatternsValid: boolean, setIsShiftPatternsValid] = useState(false);

  function modalTitle(): string {
    return props.isEdit
      ? `${props.t("EDIT_ABSENCE")} #${props.absence?.id || ""}`
      : props.t("REPORT_NEW_ABSENCE");
  }

  function haveCurrentAbsence(): boolean {
    return props.absence !== null;
  }

  function isAllValid(): boolean {
    // check in the data from the 3 steps is collected and validated
    // if main details and classifications are valid, "isValid" will be TRUE
    return props.isValid && isShiftPatternsValid && isQuestionsValid() && haveCurrentAbsence();
  }

  function isQuestionsValid() {
    const mandatoryStandardQuestions = (props.standardQuestions || []).filter(q => q.mandatory);
    const mandatoryDynamicQuestions = (props.dynamicQuestions || []).filter(q => q.mandatory);
    const standardAnswers = questions.standard?.answers;
    const dynamicAnswers = questions.dynamic?.answers;

    if (
      (mandatoryDynamicQuestions.length && !dynamicAnswers) ||
      (mandatoryStandardQuestions.length && !standardAnswers)
    ) {
      return false;
    }

    const hasAnswer = entry => {
      const answer = entry?.answer;
      if (Array.isArray(answer)) {
        return answer.length > 0;
      }
      if (typeof answer === "string") {
        return answer.trim().length > 0;
      }
      return Boolean(answer) || answer === false;
    };

    const standardQestionsAreValid = mandatoryStandardQuestions.every(q =>
      hasAnswer(standardAnswers[q.id]),
    );
    const dynamicQestionsAreValid = mandatoryDynamicQuestions.every(q =>
      hasAnswer(dynamicAnswers[q.id]),
    );

    const referralsAreValid = questions.referrals?.isValid !== false;

    return standardQestionsAreValid && dynamicQestionsAreValid && referralsAreValid;
  }

  function handleCancelAbsence(): void {
    props.setShowCancelMyAbsencesModal(true);
  }

  function handleCloseModal(): void {
    props.onClose();
    props.onUpdateDraft(null);
  }

  function handleSubmit(): void {
    const standardAnswers = questions.standard.answers;
    const dynamicAnswers = questions.dynamic.answers;

    const dynamicPayload = dynamicAnswers
      ? {
          clientId: props.clientId,
          interactionId: props.absence?.interactionId,
          data: Object.keys(dynamicAnswers).map(prop => ({
            id: +prop,
            answer: {
              value: dynamicAnswers[prop].answer,
              type: (props.dynamicQuestions || []).find(q => q.id === +prop)?.type,
            },
          })),
        }
      : null;

    if (standardAnswers) {
      const preparedStandardAnswers = Object.keys(standardAnswers).map(prop => ({
        id: +prop,
        answer: {
          value: standardAnswers[prop].answer,
          type: (props.standardQuestions || []).find(q => q.id === +prop)?.type,
        },
      }));

      props.submitStandardQuestions({
        clientId: props.clientId,
        interactionId: props.absence?.interactionId,
        data: preparedStandardAnswers,
        nextDynamicQuestions: dynamicPayload,
      });
    } else if (dynamicPayload) {
      props.submitDynamicQuestions(dynamicPayload);
    }

    if (questions.additionalInfo) {
      props.submitAdditionalQuestion({
        clientId: props.clientId,
        interactionId: props.absence?.interactionId,
        data: {
          additionalInfo: questions.additionalInfo,
        },
      });
    }
    props.onClose();
  }

  function updateAnswers(answers: Object) {
    setQuestions({
      standard: {},
      dynamic: {},
      referrals: {},
      additionalInfo: "",
      ...questions,
      ...answers,
    });
  }
  return (
    <Modal className="modal-large">
      <ModalHeader className="d-flex justify-content-between" toggle={handleCloseModal}>
        {modalTitle()}
      </ModalHeader>
      <ModalBody className="d-flex flex-column align-content-center">
        <LinearWizard defaultStep={props.defaultStep} data={[]}>
          <Step
            component={MyAbsencesMainDetails}
            title={`1/4 ${props.t("TYPE_AND_DURATION")}`}
            clientId={props.clientId}
            isReadonly={allowCancelAbsence}
          />
          <Step
            component={MyAbsenceShiftPatterns}
            title={`2/4 ${props.t("SHIFT_PATTERN")}`}
            setIsValid={valid => {
              setIsShiftPatternsValid(valid);
            }}
            isEdit={props.isEdit}
            isReadonly={allowCancelAbsence}
          />
          <Step
            component={MyClassifications}
            title={`3/4 ${props.t("CLASSIFICATIONS")}`}
            clientId={props.clientId}
            isEdit={props.isEdit}
            absenceId={props.absence?.id}
            isReadonly={allowCancelAbsence}
          />
          <Step
            component={MyAdditionalInformation}
            title={`4/4 ${props.t("ADDITIONAL_INFORMATION")}`}
            clientId={props.clientId}
            answers={questions}
            updateAnswers={updateAnswers}
          />
        </LinearWizard>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between">
        <div className="pl-4">
          {allowCancelAbsence && (
            <Button color="negative" onClick={handleCancelAbsence}>
              {props.t("CANCEL_ABSENCE")}
            </Button>
          )}
        </div>
        <div className="d-flex justify-content-between">
          <Button color="secondary" onClick={handleCloseModal}>
            {props.t("CLOSE")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!(isAllValid() && props.absence?.interactionId)}
            materialIcon="save"
          >
            {props.t("SAVE")}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}
const mapStateToProps = (state): PropsFromState => ({
  schema: selectors.myAbsenceSchema(state),
  draft: selectors.myAbsenceDraft(state),
  isValid: selectors.isMainDetailClassificationsValid(state),
  absence: selectors.myCurrentAbsence(state),
  standardQuestions: selectors.myAbsenceStandardQuestion(state),
  dynamicQuestions: selectors.myAbsenceDynamicQuestion(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getMyClassificationsSchema: actionCreators.GetMyClassificationsSchema,
  getActiveShiftPattern: actionCreators.GetUserActiveShiftPattern,
  getEmployeeBankHolidays: sharedActionCreators.GetEmployeeBankHolidaysOptions,
  onUpdateDraft: actionCreators.SetMyAbsenceDraft,
  setShowCancelMyAbsencesModal: actionCreators.SetShowCancelMyAbsencesModal,
  submitDynamicQuestions: actionCreators.AddMyDynamicQuestions,
  submitStandardQuestions: actionCreators.AddMyStandardQuestions,
  submitAdditionalQuestion: actionCreators.AddMyAdditionalInfoQuestion,
  getAccountClientServiceSettings: accountActionCreators.GetAccountClientServiceSettings,
  getAccountAbsenceManagementSettings: accountActionCreators.GetAccountAbsenceManagementSettings,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AddMyAbsencesModal);
